import { combineReducers, ReducersMapObject } from 'redux'
import { Reducer } from 'interfaces'
import { Timeframe } from 'interfaces/chart'
import { DEFAULT_TIMEFRAMES, ALL_TIMEFRAMES } from 'constants/datetime'
import { SET_TIMEFRAME, SET_FILTER_CATEGORY, SET_TREND_MODE } from 'actions/globalActions'
import { TimeframedPage, CategorizedPage, OpportunitiesPageType } from 'interfaces/pages'
import { CategoryId, OpportunityCategoryId } from 'interfaces/meta'
import { TrendRankMode } from 'interfaces/rank'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { INSIGHT_CTA_CLICK } from 'actions/insightsActions'
import { getAnchorPage, getAnchorBlock } from 'helpers/pageHelpers'
import { hasCTAFilters, getCTATimeframe, getCTACategory } from 'helpers/insightHelpers'
import { HIGHLIGHT_CATEGORY } from 'actions/globalActions'
import { HighlightCategoryPageType } from 'interfaces/pages'
import { ToggleComparisonAction } from 'components/features/pageFilters/actions'

export function createTimeframedState<TPiece> (reducerCreator: (timeframe: Timeframe) => Reducer<TPiece>) {
  return combineReducers<Record<Timeframe, TPiece>>(
    ALL_TIMEFRAMES.reduce((result, timeframe: Timeframe) => Object.assign(result, {
      [timeframe]: reducerCreator(timeframe),
    }), {} as ReducersMapObject<Record<Timeframe, TPiece>>)
  )
}

export const createTimeframeReducer = (pageType: TimeframedPage): Reducer<Timeframe> =>
  (state = DEFAULT_TIMEFRAMES[pageType], action) => {
    switch (action.type) {
      case SET_TIMEFRAME: {
        return action.payload.type === pageType
          ? action.payload.timeframe
          : state
      }

      case INSIGHT_CTA_CLICK: {
        const { cta } = action.payload

        if (!hasCTAFilters(cta)) {
          return state
        }

        const targetPage = getAnchorPage(cta.target)
        if (targetPage !== pageType) {
          return state
        }

        const nextTimeframe = getCTATimeframe(cta.filters)
        return nextTimeframe || state
      }

      default:
        return state
    }
  }

export function createFilterCategoryReducer (pageType: OpportunitiesPageType, defaultCategoryId?: OpportunityCategoryId): Reducer<OpportunityCategoryId>
export function createFilterCategoryReducer (pageType: CategorizedPage, defaultCategoryId?: CategoryId): Reducer<CategoryId>
export function createFilterCategoryReducer (pageType: string, defaultCategoryId: CategoryId = ALL_CATEGORY_ID): Reducer<string> {
  return (state = defaultCategoryId, action) => {
    switch (action.type) {
      case SET_FILTER_CATEGORY: {
        return action.payload.type === pageType
          ? action.payload.category
          : state
      }

      case INSIGHT_CTA_CLICK: {
        const { cta } = action.payload

        if (!hasCTAFilters(cta)) {
          return state
        }

        const targetPage = getAnchorPage(cta.target)
        if (targetPage !== pageType) {
          return state
        }

        const nextFilterCategory = getCTACategory(cta.filters)
        return nextFilterCategory || state
      }

      default:
        return state
    }
  }
}

export const createTrendModeReducer = (pageType: TimeframedPage): Reducer<TrendRankMode> =>
  (prevState = 'trend', action) => {
    switch (action.type) {
      case ToggleComparisonAction.type: {
        if (action.meta !== 'success') {
          return prevState
        }

        if (action.payload.params.isEnabled) {
          return prevState
        }

        return 'trend'
      }

      case SET_TREND_MODE: {
        return action.payload.type === pageType
          ? action.payload.mode
          : prevState
      }

      case SET_FILTER_CATEGORY: {
        return action.payload.type === pageType && action.payload.category !== ALL_CATEGORY_ID
          ? 'trend'
          : prevState
      }

      case INSIGHT_CTA_CLICK: {
        if (action.payload.type === 'link') {
          const { target } = action.payload.cta
          const page = getAnchorPage(target)
          if (page === pageType) {
            const block = getAnchorBlock(target)
            if (block === 'trend' || block === 'rank') {
              return block
            }
          }
        }

        if (action.payload.type === 'set_tracker' || action.payload.type === 'edit_tracker') {
          const page = getAnchorPage(action.payload.cta.target)
          if (page === pageType) {
            return 'trend'
          }
        }

        return prevState
      }

      default:
        return prevState
    }
  }

export const createHighlightedCategoryReducer = (flowType: HighlightCategoryPageType): Reducer<CategoryId> =>
  (state = ALL_CATEGORY_ID, action) => {
    if (action.type === HIGHLIGHT_CATEGORY && action.payload.type === flowType) {
      return action.payload.category || ALL_CATEGORY_ID
    }

    return state
  }
